import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { useExpenseStore } from "../store/useExpenseStore";

const UserDetailsContext = createContext(undefined);

const TOKEN_STORAGE_KEY = "token";
const TEAM_CONTACT_STORAGE_KEY = "TeamContactID";
const BUSINESS_ID_STORAGE_KEY = "BusinessID";

export const UserDetailsProvider = ({ children }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY));
  const [account_id, setAccountId] = useState(null);
  const [version, setVersion] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const fetchInProgressRef = useRef(false);

  const location = useLocation();

  // Extract parameters from URL
  const extractParamsFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return {
      version: urlParams.get("version"),
      token: urlParams.get("token") || urlParams.get("deviceToken"),
      account_id: urlParams.get("account_id"),
    };
  };

  useEffect(() => {
    const { account_id, version, token } = extractParamsFromUrl();
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (account_id) setAccountId(account_id);
    if (version) setVersion(version);

    if (token) {
      setToken(token);
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      if (storedToken && storedToken !== token) {
        setToken(storedToken);
      }
    }
  }, [location.search]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const hasTokenInUrl = urlParams.has("token") || urlParams.has("deviceToken");

    if (!hasTokenInUrl) return;

    const timeoutId = window.setTimeout(() => {
      const nextParams = new URLSearchParams(window.location.search);
      nextParams.delete("token");
      nextParams.delete("deviceToken");

      const nextQuery = nextParams.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`;
      window.history.replaceState(null, "", nextUrl);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [location.search]);

  useEffect(() => {
    const getUserDetailsApi = (authToken) =>
      fetch(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/sso/GetUserDetails?token=${authToken}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }
      );

    const refreshTokenApi = async (teamContactID) => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/sso/GetUserDetailsByTeamContactID`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ TeamContactID: String(teamContactID) }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to refresh token. Status: ${response.status}`);
      }

      return response.json();
    };

    const fetchUserDetails = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      if (fetchInProgressRef.current) {
        return;
      }

      fetchInProgressRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        let response = await getUserDetailsApi(token);

        if (response.status === 401) {
          const storedTeamContactID =
            localStorage.getItem(TEAM_CONTACT_STORAGE_KEY) ||
            sessionStorage.getItem(TEAM_CONTACT_STORAGE_KEY);

          if (!storedTeamContactID) {
            throw new Error("TeamContactID missing for token refresh");
          }

          const refreshData = await refreshTokenApi(storedTeamContactID);
          const newToken =
            refreshData?.Token || refreshData?.token || refreshData?.accessToken;

          if (!newToken) {
            throw new Error("Token refresh failed");
          }

          localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
          setToken(newToken);

          response = await getUserDetailsApi(newToken);
        }

        if (!response.ok) {
          throw new Error(
            `Failed to fetch user details. Status: ${response.status}`
          );
        }

        const data = await response.json();

        if (!data || !data.TeamContactID || !data.BusinessID) {
          throw new Error("Invalid user details received");
        }

        localStorage.setItem(
          TEAM_CONTACT_STORAGE_KEY,
          String(data.TeamContactID)
        );
        localStorage.setItem(BUSINESS_ID_STORAGE_KEY, String(data.BusinessID));

        try {
          await useExpenseStore.getState().refreshDataForUser({
            BusinessID: data.BusinessID,
            TeamContactID: data.TeamContactID,
            UserRole: data.UserRole,
          });
        } catch (refreshError) {
          console.error("Failed to prefetch role-based expense data", refreshError);
        }

        setUserDetails(data);
        setError(null);
      } catch (err) {
        setError(err?.message || "Failed to fetch user details.");
        setUserDetails(null);
      } finally {
        fetchInProgressRef.current = false;
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [token]);

  // Show spinner/white screen if redirecting (no token found)
  if (isRedirecting) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
        }}
      >
        <img src="/AurumiLogo.png" alt="Aurumi Logo" width="48" style={{ marginRight: "1rem" }} />
        <span style={{ fontSize: "1.5rem", fontWeight: 500, color: "#444" }}>Redirecting...</span>
      </div>
    );
  }

  // Error state with Sign In button
  if (!isLoading && error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center space-y-4">
        <div className="text-lg font-semibold text-red-600">
          {error}
        </div>
        <button
          className="px-4 py-2 abc text-white rounded hover:bg-blue-700"
          onClick={() => window.location.href = "https://accounts.aurumi.ai"}
        >
          Sign In
        </button>
      </div>
    );
  }

  const value = {
    userDetails,
    isLoading,
    error,
    token,
    account_id,
    version,
  };

  return (
    <UserDetailsContext.Provider value={value}>
      {children}
    </UserDetailsContext.Provider>
  );
};

export const useUserDetails = () => {
  const context = useContext(UserDetailsContext);
  if (!context) {
    throw new Error("useUserDetails must be used within a UserDetailsProvider");
  }
  return context;
};

export default UserDetailsContext;